import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Crown,
  Info,
  Pencil,
  Plus,
  ShieldAlert,
  Trash2,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { AppLayout } from '@/components/Layout/AppLayout';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { useConfirm } from '@/components/common/ConfirmDialog';
import {
  listMembers,
  ladeEin,
  zieheEinladungZurueck,
  setMemberRole,
  removeMember,
  transferOwnership,
  fetchAvailableRoles,
  createRole,
  updateRole,
  deleteRole,
  type Member,
  type MemberRole,
} from '@/api/members.api';

// ─── Mitglieder eines Stammbaums ────────────────────────────────────────────
//
// Sichtbar nur für den Besitzer. Zwei Dinge stehen hier nebeneinander:
// wer mitarbeitet, und welche Rollen es dafür gibt. Beides gehört zusammen –
// eine Rolle ohne Träger ist so wenig aussagekräftig wie ein Mitglied ohne
// erkennbare Rechte.

const MITGLIEDER = ['members'] as const;
const ROLLEN = ['members', 'roles'] as const;

export function MembersPage() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const [zeigeAufnahme, setZeigeAufnahme] = useState(false);
  const [rolleImFormular, setRolleImFormular] = useState<'neu' | MemberRole | null>(null);

  // networkMode 'always': ohne das pausiert React Query die Abfrage, sobald es
  // sich für offline hält – und zeigt weder Daten noch Fehler, sondern hält die
  // Seite dauerhaft im leeren Gerüst fest. Bei einer Seite, deren Zweck eine
  // Rechteprüfung ist, ist stummes Warten die schlechteste aller Antworten.
  // Die Anfragen gehen an die eigene Herkunft; scheitern sie, sagt der Fehler
  // mehr als eine Pause.
  //
  // retry false: eine 403 wird beim zweiten Versuch nicht freundlicher.
  const mitglieder = useQuery({
    queryKey: MITGLIEDER,
    queryFn: listMembers,
    networkMode: 'always',
    retry: false,
  });
  const rollen = useQuery({
    queryKey: ROLLEN,
    queryFn: fetchAvailableRoles,
    networkMode: 'always',
    retry: false,
  });

  const neuLaden = () => {
    queryClient.invalidateQueries({ queryKey: MITGLIEDER });
    queryClient.invalidateQueries({ queryKey: ROLLEN });
  };

  const rolleAendern = useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) =>
      setMemberRole(userId, roleId),
    onSuccess: () => {
      neuLaden();
      toast.success('Rolle geändert.');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const entfernen = useMutation({
    mutationFn: (userId: string) => removeMember(userId),
    onSuccess: () => {
      neuLaden();
      toast.success('Mitglied entfernt.');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const uebergeben = useMutation({
    mutationFn: (userId: string) => transferOwnership(userId),
    onSuccess: (d) => {
      neuLaden();
      // Der Besitz ist weg – auch die Baumliste stimmt nicht mehr.
      queryClient.invalidateQueries({ queryKey: ['trees'] });
      toast.success(`Stammbaum an ${d.newOwner} übergeben.`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const zuruecknehmen = useMutation({
    mutationFn: (id: string) => zieheEinladungZurueck(id),
    onSuccess: () => {
      neuLaden();
      toast.success('Einladung zurückgezogen.');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rolleLoeschen = useMutation({
    mutationFn: (roleId: string) => deleteRole(roleId),
    onSuccess: () => {
      neuLaden();
      toast.success('Rolle gelöscht.');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const mitgliedEntfernen = async (m: Member) => {
    const ok = await confirm({
      title: 'Mitglied entfernen?',
      message: (
        <>
          <strong>{m.email}</strong> verliert den Zugang zu diesem Stammbaum. Das Konto
          selbst bleibt bestehen, ebenso alles, was die Person eingetragen hat.
        </>
      ),
      confirmLabel: 'Entfernen',
      variant: 'danger',
    });
    if (ok) entfernen.mutate(m.userId);
  };

  const einladungZuruecknehmen = async (e: { id: string; email: string }) => {
    const ok = await confirm({
      title: 'Einladung zurückziehen?',
      message: (
        <>
          Der Link, den <strong>{e.email}</strong> per E-Mail bekommen hat, wird
          damit ungültig. Sie können jederzeit erneut einladen.
        </>
      ),
      confirmLabel: 'Zurückziehen',
      variant: 'danger',
    });
    if (ok) zuruecknehmen.mutate(e.id);
  };

  const besitzUebergeben = async (m: Member) => {
    const ok = await confirm({
      title: 'Stammbaum übergeben?',
      message: (
        <>
          <strong>{m.email}</strong> wird Besitzer dieses Stammbaums und verwaltet ab
          dann Mitglieder und Rollen. Sie selbst bleiben Mitglied mit Ihrer bisherigen
          Rolle – <strong>diese Seite sehen Sie danach nicht mehr</strong>. Rückgängig
          machen kann das nur der neue Besitzer.
        </>
      ),
      confirmLabel: 'Übergeben',
      variant: 'danger',
    });
    if (ok) uebergeben.mutate(m.userId);
  };

  const eigeneRolleLoeschen = async (r: MemberRole) => {
    const ok = await confirm({
      title: 'Rolle löschen?',
      message: (
        <>
          Die Rolle <strong>„{r.name}"</strong> wird entfernt. Mitglieder, die sie
          tragen, müssen vorher eine andere bekommen.
        </>
      ),
      confirmLabel: 'Löschen',
      variant: 'danger',
    });
    if (ok) rolleLoeschen.mutate(r.id);
  };

  // ─── Kein Besitzer? Dann gehört diese Seite nicht hierher ─────────────────
  //
  // Der Server antwortet mit 403. Statt einer nackten Fehlermeldung sagen wir,
  // woran es liegt – ein Mitglied kann daraus etwas machen, „Keine
  // Berechtigung" nicht.
  // Am Statuscode festgemacht, nicht am Text der Meldung: eine 403 bleibt eine
  // 403, auch wenn jemand die Formulierung ändert oder übersetzt.
  const keinBesitzer =
    mitglieder.isError && (mitglieder.error as { status?: number })?.status === 403;

  if (keinBesitzer) {
    return (
      <AppLayout>
        <div className="mx-auto max-w-lg px-4 py-12 sm:px-6">
          <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" />
            <div className="text-sm leading-relaxed text-slate-600">
              <h1 className="text-base font-semibold text-slate-900">
                Nur der Besitzer verwaltet die Mitglieder
              </h1>
              <p className="mt-2">
                Dieser Stammbaum gehört jemand anderem. Wer Zugang bekommt und mit
                welchen Rechten, entscheidet die Person, der er gehört.
              </p>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <header className="mb-6">
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
            <Users className="h-6 w-6 text-blue-600" aria-hidden="true" />
            Mitglieder
          </h1>
          <p className="mt-1 text-sm leading-relaxed text-slate-500">
            Wer an diesem Stammbaum mitarbeitet und mit welchen Rechten. Neue Konten
            legt der Betreiber an – hier laden Sie bestehende ein. Mitglied wird
            jemand erst, wenn er zustimmt.
          </p>
        </header>

        {/* ─── Mitglieder ─────────────────────────────────────────────── */}

        <div className="mb-4 flex justify-end">
          <button
            onClick={() => setZeigeAufnahme(true)}
            className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
          >
            <UserPlus className="h-4 w-4" aria-hidden="true" />
            Einladen
          </button>
        </div>

        {mitglieder.isLoading && (
          <div className="flex justify-center py-12">
            <LoadingSpinner message="Mitglieder werden geladen..." />
          </div>
        )}

        {mitglieder.isError && !keinBesitzer && (
          <ErrorMessage
            message={(mitglieder.error as Error)?.message ?? 'Laden fehlgeschlagen.'}
            onRetry={() => mitglieder.refetch()}
          />
        )}

        {mitglieder.data && (
          <ul className="space-y-3">
            {mitglieder.data.mitglieder.map((m) => (
              <li
                key={m.userId}
                className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-slate-900">{m.email}</span>
                      {m.isOwner && (
                        <span className="flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                          <Crown className="h-3 w-3" aria-hidden="true" />
                          Besitzer
                        </span>
                      )}
                      {!m.isActive && (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">
                          deaktiviert
                        </span>
                      )}
                    </div>

                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {m.isOwner ? (
                        // Die Rechte des Besitzers folgen aus dem Besitz, nicht
                        // aus einer Rolle. Ein Auswahlfeld würde etwas anderes
                        // behaupten.
                        <span className="text-xs text-slate-500">
                          Alle Rechte außer Gesundheitsdaten – aus dem Besitz.
                        </span>
                      ) : (
                        <>
                          <label
                            htmlFor={`rolle-${m.userId}`}
                            className="text-xs text-slate-500"
                          >
                            Rolle
                          </label>
                          <select
                            id={`rolle-${m.userId}`}
                            value={m.role.id}
                            disabled={rolleAendern.isPending}
                            onChange={(e) =>
                              rolleAendern.mutate({ userId: m.userId, roleId: e.target.value })
                            }
                            className="rounded border border-slate-300 px-2 py-1 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:opacity-50"
                          >
                            {(rollen.data?.data ?? [m.role]).map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.name}
                                {r.eigene ? '' : ' (Vorlage)'}
                              </option>
                            ))}
                          </select>
                        </>
                      )}
                    </div>
                  </div>

                  {!m.isOwner && (
                    <div className="flex shrink-0 gap-1">
                      <button
                        onClick={() => besitzUebergeben(m)}
                        disabled={uebergeben.isPending || !m.isActive}
                        className="rounded p-2 text-slate-400 hover:bg-amber-50 hover:text-amber-600 disabled:opacity-40"
                        aria-label={`Stammbaum an ${m.email} übergeben`}
                        title="Stammbaum übergeben"
                      >
                        <Crown className="h-4 w-4" aria-hidden="true" />
                      </button>
                      <button
                        onClick={() => mitgliedEntfernen(m)}
                        disabled={entfernen.isPending}
                        className="rounded p-2 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                        aria-label={`${m.email} entfernen`}
                        title="Entfernen"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* ─── Offene Einladungen ─────────────────────────────────────── */}
        {/*
            Gehören auf dieselbe Seite. Stünden sie woanders, hätte der
            Besitzer nach dem Einladen eine Liste vor sich, in der sich nichts
            geändert hat – und lüde ein zweites Mal ein.
        */}
        {mitglieder.data && mitglieder.data.einladungen.length > 0 && (
          <section className="mt-8">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Wartet auf Zustimmung
            </h2>
            <ul className="mt-3 space-y-2">
              {mitglieder.data.einladungen.map((e) => (
                <li
                  key={e.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3"
                >
                  <div className="min-w-0">
                    <span className="font-medium text-slate-700">{e.email}</span>
                    <p className="mt-0.5 text-xs text-slate-500">
                      Vorgesehene Rolle: {e.rolleName} · eingeladen am{' '}
                      {new Date(e.eingeladenAm).toLocaleDateString('de-DE')} · gültig bis{' '}
                      {new Date(e.gueltigBis).toLocaleDateString('de-DE')}
                    </p>
                  </div>
                  <button
                    onClick={() => einladungZuruecknehmen(e)}
                    disabled={zuruecknehmen.isPending}
                    className="rounded p-2 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                    aria-label={`Einladung an ${e.email} zurückziehen`}
                    title="Einladung zurückziehen"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* ─── Rollen ─────────────────────────────────────────────────── */}

        <section className="mt-10">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Rollen</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                Vorlagen kommen vom Betreiber und gelten überall. Eigene Rollen gehören
                nur diesem Stammbaum.
              </p>
            </div>
            <button
              onClick={() => setRolleImFormular('neu')}
              className="flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Eigene Rolle
            </button>
          </div>

          {rollen.data && (
            <ul className="space-y-2">
              {rollen.data.data.map((r) => (
                <li
                  key={r.id}
                  className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-slate-900">{r.name}</span>
                      {!r.eigene && (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">
                          Vorlage
                        </span>
                      )}
                    </div>
                    {r.description && (
                      <p className="mt-0.5 text-sm text-slate-600">{r.description}</p>
                    )}
                    <p className="mt-0.5 text-xs text-slate-400">
                      {r.permissions.length}{' '}
                      {r.permissions.length === 1 ? 'Berechtigung' : 'Berechtigungen'}
                    </p>
                  </div>

                  {r.eigene && (
                    <div className="flex shrink-0 gap-1">
                      <button
                        onClick={() => setRolleImFormular(r)}
                        className="rounded p-2 text-slate-400 hover:bg-blue-50 hover:text-blue-600"
                        aria-label={`${r.name} bearbeiten`}
                        title="Bearbeiten"
                      >
                        <Pencil className="h-4 w-4" aria-hidden="true" />
                      </button>
                      <button
                        onClick={() => eigeneRolleLoeschen(r)}
                        className="rounded p-2 text-slate-400 hover:bg-red-50 hover:text-red-600"
                        aria-label={`${r.name} löschen`}
                        title="Löschen"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {zeigeAufnahme && (
        <AufnahmeModal
          rollen={rollen.data?.data ?? []}
          onClose={() => setZeigeAufnahme(false)}
          onDone={() => {
            setZeigeAufnahme(false);
            neuLaden();
          }}
        />
      )}

      {rolleImFormular && rollen.data && (
        <RollenModal
          rolle={rolleImFormular === 'neu' ? null : rolleImFormular}
          gruppen={rollen.data.groups}
          sensitive={rollen.data.sensitive}
          onClose={() => setRolleImFormular(null)}
          onDone={() => {
            setRolleImFormular(null);
            neuLaden();
          }}
        />
      )}
    </AppLayout>
  );
}

// ─── Mitglied aufnehmen ─────────────────────────────────────────────────────

function AufnahmeModal({
  rollen,
  onClose,
  onDone,
}: {
  rollen: MemberRole[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState(rollen[0]?.id ?? '');

  const mutation = useMutation({
    mutationFn: () => ladeEin(email.trim(), roleId),
    onSuccess: (e) => {
      // Ehrlich bleiben: die Person ist noch KEIN Mitglied. „Aufgenommen" wäre
      // an dieser Stelle eine Falschmeldung – und der Besitzer wunderte sich,
      // warum sie in der Liste anders aussieht.
      toast.success(
        e.benachrichtigt
          ? `Einladung an ${e.email} verschickt. Mitglied wird sie, sobald sie zustimmt.`
          : `Einladung für ${e.email} steht. Es ging KEINE E-Mail hinaus – ` +
            'ohne Mailserver findet sie die Einladung nur in der Anwendung.',
      );
      onDone();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Modal titel="Zum Stammbaum einladen" onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!roleId) {
            toast.error('Bitte eine Rolle wählen.');
            return;
          }
          mutation.mutate();
        }}
        className="space-y-4 px-5 py-4"
      >
        <div>
          <label htmlFor="mitglied-email" className="mb-1 block text-sm font-medium text-slate-700">
            E-Mail des bestehenden Kontos *
          </label>
          <input
            id="mitglied-email"
            type="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="person@example.com"
            className="w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
          <p className="mt-1 text-xs text-slate-400">
            Die Person muss bereits ein Konto haben. Neue Konten legt der Betreiber an.
            Sie bekommt eine E-Mail und entscheidet selbst – bis dahin sieht sie von
            diesem Stammbaum nichts.
          </p>
        </div>

        <div>
          <label htmlFor="mitglied-rolle" className="mb-1 block text-sm font-medium text-slate-700">
            Rolle
          </label>
          <select
            id="mitglied-rolle"
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            className="w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            {rollen.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
                {r.eigene ? '' : ' (Vorlage)'}
              </option>
            ))}
          </select>
        </div>

        <ModalAktionen
          onClose={onClose}
          label={mutation.isPending ? 'Wird verschickt…' : 'Einladen'}
          isPending={mutation.isPending}
        />
      </form>
    </Modal>
  );
}

// ─── Eigene Rolle anlegen/bearbeiten ────────────────────────────────────────

function RollenModal({
  rolle,
  gruppen,
  sensitive,
  onClose,
  onDone,
}: {
  rolle: MemberRole | null;
  gruppen: { key: string; label: string; description?: string; view: string; manage?: string }[];
  sensitive: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState(rolle?.name ?? '');
  const [beschreibung, setBeschreibung] = useState(rolle?.description ?? '');
  const [rechte, setRechte] = useState<string[]>(rolle?.permissions ?? []);

  const umschalten = (key: string) =>
    setRechte((alt) => (alt.includes(key) ? alt.filter((k) => k !== key) : [...alt, key]));

  const mutation = useMutation({
    mutationFn: () => {
      const input = {
        name: name.trim(),
        description: beschreibung.trim() || null,
        permissions: rechte,
      };
      return rolle ? updateRole(rolle.id, input) : createRole(input);
    },
    onSuccess: () => {
      toast.success(rolle ? 'Rolle gespeichert.' : 'Rolle angelegt.');
      onDone();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const sensibelGewaehlt = rechte.some((r) => sensitive.includes(r));

  return (
    <Modal titel={rolle ? 'Rolle bearbeiten' : 'Eigene Rolle'} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) {
            toast.error('Name ist erforderlich.');
            return;
          }
          mutation.mutate();
        }}
        className="space-y-4 px-5 py-4"
      >
        <div>
          <label htmlFor="rolle-name" className="mb-1 block text-sm font-medium text-slate-700">
            Name *
          </label>
          <input
            id="rolle-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z. B. Fotoarchiv"
            autoFocus
            className="w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </div>

        <div>
          <label htmlFor="rolle-beschreibung" className="mb-1 block text-sm font-medium text-slate-700">
            Beschreibung
          </label>
          <input
            id="rolle-beschreibung"
            value={beschreibung ?? ''}
            onChange={(e) => setBeschreibung(e.target.value)}
            className="w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </div>

        <fieldset>
          <legend className="mb-2 text-sm font-medium text-slate-700">Berechtigungen</legend>
          <div className="max-h-72 space-y-3 overflow-y-auto rounded border border-slate-200 p-3">
            {gruppen.map((g) => {
              const eintraege = [
                { key: g.view, label: 'Ansehen' },
                ...(g.manage ? [{ key: g.manage, label: 'Bearbeiten' }] : []),
              ];
              const heikel = eintraege.some((e) => sensitive.includes(e.key));
              return (
                <div key={g.key}>
                  <p className="flex items-center gap-1.5 text-sm font-medium text-slate-800">
                    {g.label}
                    {heikel && (
                      <span className="flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                        <ShieldAlert className="h-3 w-3" aria-hidden="true" />
                        Gesundheitsdaten
                      </span>
                    )}
                  </p>
                  {g.description && (
                    <p className="text-xs text-slate-400">{g.description}</p>
                  )}
                  <div className="mt-1 flex gap-4">
                    {eintraege.map((e) => (
                      <label key={e.key} className="flex items-center gap-1.5 text-sm text-slate-600">
                        <input
                          type="checkbox"
                          checked={rechte.includes(e.key)}
                          onChange={() => umschalten(e.key)}
                          className="rounded border-slate-300"
                        />
                        {e.label}
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {sensibelGewaehlt && (
            <p className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 p-2.5 text-xs leading-relaxed text-amber-900">
              <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              Diese Rolle schließt Gesundheitsdaten ein. Wer sie trägt, sieht Diagnosen
              und Merkmale der Personen in diesem Stammbaum.
            </p>
          )}
        </fieldset>

        <ModalAktionen
          onClose={onClose}
          label={mutation.isPending ? 'Speichern…' : 'Speichern'}
          isPending={mutation.isPending}
        />
      </form>
    </Modal>
  );
}

// ─── Gemeinsamer Rahmen ─────────────────────────────────────────────────────

function Modal({
  titel,
  onClose,
  children,
}: {
  titel: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-slate-900">{titel}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100"
            aria-label="Schließen"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalAktionen({
  onClose,
  label,
  isPending,
}: {
  onClose: () => void;
  label: string;
  isPending: boolean;
}) {
  return (
    <div className="flex items-center justify-end gap-3 border-t border-slate-200 pt-4">
      <button
        type="button"
        onClick={onClose}
        className="rounded-md px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
      >
        Abbrechen
      </button>
      <button
        type="submit"
        disabled={isPending}
        className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
      >
        {label}
      </button>
    </div>
  );
}

export default MembersPage;
